import React, { useState } from 'react';

import { AlertList, ErrorResponse, ModalCard, Spinner } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';

interface ComponentProps {
  id: string;
  header: string;
  isFetching: boolean;
  error?: unknown;
  errorMessage?: string;
  /** True when this step has nothing left to do, because a previous run already did it. */
  isComplete: boolean;
  /** What stands once the provider confirms, and what stands if they stop here instead. */
  outcome: string[];
  /**
   * The one sentence that has to be impossible to miss: what pressing the button writes.
   *
   * Separated from `outcome` rather than being its last bullet because it is a different kind of
   * statement. The outcome list describes a change; this warns that the change is about to happen
   * and is not undoable from inside the assistant. A caution buried as the third item of a blue
   * information panel is not a caution.
   */
  warning: string;
  /** Shown in place of the table when the step found nothing at all to work on. */
  emptyMessage: string;
  /** Shown instead of the table when every candidate is already carried across. */
  completeMessage: string;
  hasRows: boolean;
  /**
   * Shown above the card rather than inside it. A filter that decides WHICH rows the card holds
   * belongs outside it - collapsing the table to see past a long list should not take the control
   * that shortens the list away with it. For the same reason it survives the empty state: a filter
   * narrowed to a combination that matches nothing is the likeliest way to reach that state, and
   * taking the only control on the step away with the rows leaves the provider with nowhere to go
   * but closing the assistant.
   */
  filter?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The frame every step of the cutover assistant renders inside.
 *
 * Each step has the same four things to say and says them in the same order, so they live here
 * rather than being re-stated three times: what it found, what confirming will do, what stands if
 * the provider stops at this point, and whether a previous run already did the work.
 *
 * That last one matters more than it looks. The assistant commits each step as it is confirmed, so
 * a provider who stops part way through picks up by starting it again with the same two services
 * and the same cutover date. A step that is already done has to say so plainly and let them move on
 * rather than offering to do it a second time.
 */
const StepPanel: React.FC<ComponentProps> = ({
  id,
  header,
  isFetching,
  error,
  errorMessage,
  isComplete,
  outcome,
  warning,
  emptyMessage,
  completeMessage,
  hasRows,
  filter,
  children,
}) => {
  // Open on arrival. ModalCard collapses by default, which is right for the bulk-copy list it was
  // built for - there the table is a confirmation of a search the user just ran. Here the table IS
  // the step: a provider is asked to confirm a change to these exact records, so showing them a
  // closed card and asking them to go looking would be asking them to confirm blind. Still
  // collapsible, for a long list they have already read.
  const [isTableOpen, setIsTableOpen] = useState(true);

  if (isFetching) return <Spinner loading />;
  if (error) return <ErrorResponse message={errorMessage ?? 'This step could not be loaded.'} />;

  return (
    <div>
      {isComplete && hasRows && (
        <AlertMessage
          id={`${id}-complete`}
          name={`${id}-complete`}
          value={completeMessage}
          type="alert-success"
          color="success"
        />
      )}
      {filter}
      {hasRows ? (
        <>
          <ModalCard header={header} className="w-100 mb-3" isOpen={isTableOpen} setIsOpen={setIsTableOpen}>
            <div className="bulk-copy-scroll-table">{children}</div>
          </ModalCard>
          <AlertList header="Confirming this step will:" color="info" listItems={outcome} />
          <AlertMessage
            id={`${id}-warning`}
            name={`${id}-warning`}
            value={warning}
            type="alert-warning"
            color="warning"
          />
        </>
      ) : (
        <AlertMessage id={`${id}-empty`} name={`${id}-empty`} value={emptyMessage} type="alert-info" color="info" />
      )}
    </div>
  );
};

export default StepPanel;
